import type { DateRangeEntry, DateValueTuple } from "../types";

import type { AnalyticsDateRange } from "./dateBuckets";
import { buildDateEntriesForRange, compactDateRangeAxisLabels } from "./dateEntries";
import { combineDateSeries, toSeriesRecord } from "./seriesData";

import type { CustomChartResponseItem } from "@/queries/analytics-custom-charts-types";
import type { AnalyticsChartInterval } from "@/queries/useRequestAnalytics";

export interface CustomChartTimeSeriesContext {
  chartInterval?: AnalyticsChartInterval;
  countAxisTitle?: string;
  structuredDateRange?: AnalyticsDateRange;
  onDateRangeSelect?: (range: { from: Date; to: Date }) => void;
}

export interface CustomChartInteractionHandlers {
  bucketClickToFilter?: boolean;
  onCategoryClick?: (payload: { label: string; rawLabel?: string }) => void;
  onGroupedBucketClick?: (payload: {
    dateRange: { from: Date; to: Date };
    groupValue: string;
    rawGroupValue?: string;
  }) => void;
}

export type CustomChartOptionsContext = CustomChartTimeSeriesContext &
  CustomChartInteractionHandlers;

const getDateFromBucketKey = (bucketKey: string) => {
  const timestamp = Number(bucketKey);
  return Number.isFinite(timestamp) ? new Date(timestamp) : new Date(bucketKey);
};

const chartHasTimeSeriesBuckets = (chart: CustomChartResponseItem) =>
  chart.data.some(row => row.bucketKey !== undefined);

const buildCustomChartDateEntries = (
  chart: CustomChartResponseItem,
  context?: CustomChartTimeSeriesContext,
): DateRangeEntry[] => {
  const rows = [...chart.data].sort((a, b) => Number(a.bucketKey ?? 0) - Number(b.bucketKey ?? 0));
  const entriesFromData = rows
    .filter(row => row.bucketKey !== undefined)
    .map(row => {
      const key = String(row.bucketKey ?? row.label);
      return {
        date: getDateFromBucketKey(key),
        key,
        label: row.label,
      };
    });

  if (context?.chartInterval && context.structuredDateRange) {
    const rangeEntries = buildDateEntriesForRange(
      context.structuredDateRange,
      context.chartInterval,
    );
    if (entriesFromData.length === 0) {
      return compactDateRangeAxisLabels(rangeEntries);
    }
    if (entriesFromData.length < rangeEntries.length) {
      const dataByKey = new Map(entriesFromData.map(entry => [entry.key, entry]));
      return compactDateRangeAxisLabels(
        rangeEntries.map(entry => dataByKey.get(entry.key) ?? entry),
      );
    }
  }

  return compactDateRangeAxisLabels(entriesFromData);
};

export const prepareSingleSeriesRows = (
  chart: CustomChartResponseItem,
  context?: CustomChartTimeSeriesContext,
) => {
  const isTimeSeries = chartHasTimeSeriesBuckets(chart);
  if (!isTimeSeries) {
    return {
      isTimeSeries,
      plotData: chart.data.map(row => ({
        x: row.label,
        y: row.value ?? 0,
        tooltipTitle: row.label,
        rawLabel: row.rawLabel,
      })),
      dateEntries: [] as DateRangeEntry[],
    };
  }

  const dateEntries = buildCustomChartDateEntries(chart, context);
  const valueTuples = chart.data
    .filter(row => row.bucketKey !== undefined)
    .map((row): DateValueTuple => [String(row.bucketKey ?? row.label), Number(row.value ?? 0)]);
  const plotData = combineDateSeries({ y: toSeriesRecord(valueTuples) }, dateEntries, 0);

  return { isTimeSeries, plotData, dateEntries };
};

export const prepareMultiSeriesRows = (
  chart: CustomChartResponseItem,
  context?: CustomChartTimeSeriesContext,
) => {
  const isTimeSeries = chartHasTimeSeriesBuckets(chart);
  const rows = [...chart.data];
  if (!isTimeSeries) {
    return {
      isTimeSeries,
      plotData: rows.map(row => {
        const datum: Record<string, string | number | undefined> = {
          x: row.label,
          tooltipTitle: row.label,
          rawLabel: row.rawLabel,
        };
        chart.series.forEach(series => {
          datum[series.key] = Number(row[series.key] ?? 0);
        });
        return datum;
      }),
      dateEntries: [] as DateRangeEntry[],
    };
  }

  const dateEntries = buildCustomChartDateEntries(chart, context);
  const seriesRecords = chart.series.reduce<Record<string, Record<string, number>>>(
    (accumulator, series) => {
      accumulator[series.key] = rows.reduce<Record<string, number>>((seriesAccumulator, row) => {
        const xKey = String(row.bucketKey ?? row.label);
        seriesAccumulator[xKey] = Number(row[series.key] ?? 0);
        return seriesAccumulator;
      }, {});
      return accumulator;
    },
    {},
  );

  return {
    isTimeSeries,
    plotData: combineDateSeries(seriesRecords, dateEntries, 0),
    dateEntries,
  };
};
